(function () {
    /**************
     Setup
     ***************/

        // save the section id from the script tag and find the section
    const sectionID = document.currentScript.dataset.id;
    const section = document.querySelector(`[id="${sectionID}"]`);

    // exit if section is not found
    if (!section) {
        console.warn(`Product section with ID ${sectionID} not found.`);
        return;
    }

    // bundle products reference
    const products = JSON.parse(section.querySelector(".productV11JSON").textContent);

    // upsell products, kept apart from the bundle so they never count toward the gift
    const upsellProducts = JSON.parse(section.querySelector(".productV11UpsellJSON")?.textContent ?? "[]");
    const allProducts = [...products, ...upsellProducts];

    // the gift variant id, or null when no gift product is set
    const giftJSONElement = section.querySelector(".productV11GiftJSON");
    const giftVariantID = JSON.parse(giftJSONElement.textContent);

    // if gift behavior is set to threshold
    const thresholdSetting = parseInt(giftJSONElement.dataset.threshold);
    const giftThreshold = Number.isNaN(thresholdSetting) ? null : thresholdSetting;

    // element reference list
    const priceElement = section.querySelector(".productV11Price");
    const colorTitleElement = section.querySelector(".productV11ColorsTitleActive");
    const summaryElement = section.querySelector(".productV11Summary");
    const summaryTextElement = section.querySelector(".productV11SummaryText");
    const atcElement = section.querySelector(".productV11ATC");

    // productID -> size -> color -> variant, built once so availability checks never rescan the arrays
    const variantLookup = {};
    allProducts.forEach(product => {
        variantLookup[product.id] = {};
        product.variants.forEach(variant => {
            if (!variantLookup[product.id][variant.size]) {
                variantLookup[product.id][variant.size] = {};
            }
            variantLookup[product.id][variant.size][variant.color] = variant;
        });
    });

    // check for any color with no product, disable that color if none found
    section.querySelectorAll(".productV11ColorsItemInput").forEach(input => {
        input.disabled = !products.some(product => product.variants.some(variant => variant.available && matchesColor(variant,
            input.dataset.color)));
    });

    /**************
     Product Swiper
     ***************/

        // the default gallery plus one per color that carries its own images
    const galleries = new Map();

    // match active thumbs to main gallery
    function syncThumbs(gallery, swiper) {
        gallery.querySelectorAll(".productV11VisualThumbsButton").forEach(element => {
            element.classList.toggle("active", parseInt(element.dataset.index) === swiper.realIndex);
        });
    }

    // stop every gallery video so a slide or gallery change never leaves audio running
    function pauseVideos() {
        section.querySelectorAll(".productV11VisualSwiperSlideVideo").forEach(video => video.pause());
    }

    section.querySelectorAll(".productV11VisualItem").forEach(gallery => {

        // mobile thumbnails
        const thumbnailSwiper = new Swiper(gallery.querySelector(".productV11VisualSwiperThumbs"), {
            slidesPerView: 3,
            navigation: {
                nextEl: gallery.querySelector(".productV11VisualSwiperThumbsWrap .swiper-button-next"),
                prevEl: gallery.querySelector(".productV11VisualSwiperThumbsWrap .swiper-button-prev"),
            },
        });

        // desktop swiper
        const productSwiper = new Swiper(gallery.querySelector(".productV11VisualSwiper"), {
            slidesPerView: 1,
            loop: true,
            spaceBetween: 20,
            observer: true,
            observeParents: true,
            thumbs: {
                swiper: thumbnailSwiper
            },
            navigation: {
                nextEl: gallery.querySelector(".productV11VisualSwiper .swiper-button-next"),
                prevEl: gallery.querySelector(".productV11VisualSwiper .swiper-button-prev"),
            },
            on: {
                afterInit: swiper => syncThumbs(gallery, swiper),
                realIndexChange: swiper => {
                    syncThumbs(gallery, swiper);
                    pauseVideos();
                },
            },
        });

        // the play overlay starts the video, then hands over to its own controls
        gallery.querySelectorAll(".productV11VisualSwiperSlidePlay").forEach(element => {
            element.addEventListener("click", function () {
                const video = this.closest(".productV11VisualSwiperSlide").querySelector(".productV11VisualSwiperSlideVideo");
                this.remove();
                video.controls = true;
                video.play();
            });
        });

        // desktop thumbs
        gallery.querySelectorAll(".productV11VisualThumbsButton").forEach(element => {
            element.addEventListener("click", function () {
                productSwiper.slideToLoop(parseInt(this.dataset.index));
            });
        });

        galleries.set(gallery, [thumbnailSwiper, productSwiper]);
    });

    // the main loaded mixed gallery
    const defaultGallery = section.querySelector(".productV11VisualItem:not([data-color])");

    // changing to color gallery or falling back to the default
    function renderGallery(useDefault = false) {
        // if show all button, useDefault gallery
        let color, gallery;
        if (useDefault) {
            gallery = defaultGallery;
        } else {
            color = getSelectedColor();
            gallery = section.querySelector(`.productV11VisualItem[data-color="${color}"]`) ?? defaultGallery;
        }

        if (!gallery || gallery.classList.contains("active")) {
            return;
        }

        // hide current active gallery
        pauseVideos();
        section.querySelectorAll(".productV11VisualItem.active").forEach(element => element.classList.remove("active"));
        gallery.classList.add("active");

        const [thumbnailSwiper, productSwiper] = galleries.get(gallery) ?? [];
        if (!productSwiper) {
            return;
        }

        // swiper measures nothing while its gallery is hidden, so size both now they are on screen
        thumbnailSwiper.update();
        productSwiper.update();

        // reset image positions
        productSwiper.slideToLoop(0, 0);
        syncThumbs(gallery, productSwiper);
    }

    // view all button reverts back to original default gallery
    section.querySelector('.productV11Gallery').addEventListener('click', () => renderGallery(true));

    /**************
     Variant Matching
     ***************/

    // exact size and color match, falling back to products that carry no size or no color option
    function getVariant(productID, size, color) {
        const sizes = variantLookup[productID];
        if (!sizes) {
            return null;
        }

        const colors = sizes[size] ?? sizes["null"];
        if (!colors) {
            return null;
        }

        return colors[color] ?? colors["null"] ?? null;
    }

    // the selected color
    function getSelectedColor() {
        return section.querySelector(".productV11ColorsItemInput:checked")?.dataset.color ?? null;
    }

    // check match of colors
    function matchesColor(variant, color) {
        return variant.color === color || variant.color === null;
    }

    // a quantity option with no size behind it lets the color alone pick the variant. the cheapest
    // available one wins, and when none are the cheapest match still carries the sold out state
    function getQuantityVariant(productID, color) {
        const product = allProducts.find(entry => String(entry.id) === String(productID));
        const matching = product.variants.filter(variant => matchesColor(variant, color));
        const available = matching.filter(variant => variant.available);
        const pool = available.length > 0 ? available : matching;

        // both pools are copies off the filter, so sorting in place can't disturb the parsed data
        return pool.sort((first, second) => first.price - second.price)[0] ?? null;
    }

    // the variant one option maps to. sizes each resolve their own, including a quantity option tied to
    // a size, where every sizeless quantity option on a row shares the single variant the color resolves to
    function getOptionVariant(productID, input, color) {
        if (input?.dataset.quantity && !input.dataset.size) {
            return getQuantityVariant(productID, color);
        }

        return getVariant(productID, input?.dataset.size ?? null, color);
    }

    // sizes are sold one at a time, where a quantity option says how many sets it adds
    function getOptionQuantity(input) {
        return parseInt(input?.dataset.quantity) || 1;
    }

    // the cheapest in-stock price for one product in the chosen color, or null when it's sold out
    function getLowestPrice(productID, color) {
        const product = allProducts.find(entry => String(entry.id) === String(productID));
        const available = product.variants.filter(variant => variant.available && matchesColor(variant, color));

        if (available.length === 0) {
            return null;
        }

        return Math.min(...available.map(variant => variant.price));
    }

    // visual update for desktop and keeping track if items are included
    function isAdded(item) {
        if (item.querySelector(".productV11ProductsItemOptionsItemInput")) {
            return !!item.querySelector(".productV11ProductsItemOptionsItemInput:checked");
        }

        return item.classList.contains("added");
    }

    // every added row paired with the variant its option and the shared color resolve to
    function getSelections() {
        const color = getSelectedColor();
        const selections = [];

        section.querySelectorAll(".productV11ProductsItem").forEach(item => {
            // skip rows the shopper hasn't added
            if (!isAdded(item)) {
                return;
            }

            // skip rows whose option and color combination doesn't resolve to something buyable
            const optionInput = item.querySelector(".productV11ProductsItemOptionsItemInput:checked");
            const variant = getOptionVariant(item.dataset.productId, optionInput, color);
            if (!variant?.available) {
                return;
            }

            // a set option already reads as a count of pieces, so it names itself in the summary
            // rather than being reported as the one or two sets the cart actually receives
            const name = item.dataset.name;
            let summary = `1x ${name.toLowerCase()}`;
            if (optionInput?.dataset.quantity) {
                summary = optionInput.value.toLowerCase();
            }

            selections.push({
                name: name,
                option: optionInput?.value ?? "",
                // what the cart receives, which is a count of sets on a quantity row
                quantity: getOptionQuantity(optionInput),
                summary: summary,
                upsell: item.classList.contains("upsell"),
                variant: variant,
            });
        });

        return selections;
    }

    // what the selected rows come to, counting a set option once for every set it adds
    function getSelectionsTotal(selections) {
        let total = 0;
        selections.forEach(selection => {
            total += selection.variant.price * selection.quantity;
        });

        return total;
    }

    /**************
     Upsell Matching
     ***************/

    // the size of the first added bundle product, which is the size an upsell matches itself to
    function getMatchSize() {
        const inputs = section.querySelectorAll(".productV11ProductsItem:not(.upsell) .productV11ProductsItemOptionsItemInput:checked");
        return [...inputs].find(input => input.dataset.size)?.dataset.size ?? null;
    }

    // upsells left on auto follow the first added size, as long as they stock it in the chosen color
    function matchUpsells() {
        const size = getMatchSize();
        if (!size) {
            return;
        }

        section.querySelectorAll(".productV11ProductsItem.upsell").forEach(item => {
            // a size the shopper picked themselves is left alone
            if (item.dataset.match !== "auto") {
                return;
            }

            const input = item.querySelector(`.productV11ProductsItemOptionsItemInput[data-size="${CSS.escape(size)}"]`);
            if (!input || input.disabled) {
                return;
            }

            input.checked = true;
        });
    }

    /**************
     Render
     ***************/

    // disable the options with no available variant in the chosen color and drop selections that just died
    function renderAvailability() {
        const color = getSelectedColor();

        section.querySelectorAll(".productV11ProductsItem").forEach(item => {
            const productID = item.dataset.productId;
            const rowButton = item.querySelector(".productV11ProductsItemRow");
            const optionInputs = item.querySelectorAll(".productV11ProductsItemOptionsItemInput");

            // a product with nothing to pick lives or dies on the color alone
            if (optionInputs.length === 0) {
                const variant = getVariant(productID, null, color);
                rowButton.disabled = !variant?.available;
                if (rowButton.disabled) {
                    item.classList.remove("added");
                }
                return;
            }

            optionInputs.forEach(input => {
                const variant = getOptionVariant(productID, input, color);
                input.disabled = !variant?.available;

                // uncheck an option that just went out of stock so it drops out of pricing, summary and cart
                if (input.disabled && input.checked) {
                    input.checked = false;
                }
            });

            // a row with nothing left to pick can't be ordered in this color
            rowButton.disabled = [...optionInputs].every(input => input.disabled);
            if (rowButton.disabled) {
                item.classList.remove("open");
            }
        });
    }

    // the tick and the bold row name follow the added state, and the row button reports whether the
    // option list below it is open so a screen reader gets the same disclosure the shopper sees
    function renderRows() {
        section.querySelectorAll(".productV11ProductsItem").forEach(item => {
            // the chosen option is mirrored onto the row so the next click can tell a fresh pick apart
            // from a tap on the option already chosen, which is the only way a radio gets cleared
            item.dataset.selectedOption = item.querySelector(".productV11ProductsItemOptionsItemInput:checked")?.value ?? "";

            item.classList.toggle("added", isAdded(item));
            // rows with no options control nothing below them, so they are left without the disclosure state
            if (item.querySelector(".productV11ProductsItemOptionsItemInput")) {
                item.querySelector(".productV11ProductsItemRow").setAttribute("aria-expanded", item.classList.contains("open"));
            }
        });
    }

    // each row shows a from-price until an option is picked, then what that option costs
    function renderRowPrices() {
        const color = getSelectedColor();

        section.querySelectorAll(".productV11ProductsItem").forEach(item => {
            const rowPriceElement = item.querySelector(".productV11ProductsItemRowPrice");
            const lowest = getLowestPrice(item.dataset.productId, color);

            // upsell sizes show their own price under the label
            item.querySelectorAll(".productV11ProductsItemOptionsItem").forEach(option => {
                const optionPriceElement = option.querySelector(".productV11ProductsItemOptionsItemSelectorPrice");
                if (!optionPriceElement) {
                    return;
                }

                const variant = getOptionVariant(item.dataset.productId, option.querySelector(".productV11ProductsItemOptionsItemInput"), color);
                optionPriceElement.textContent = variant ? price(variant.price) : "";
            });

            if (lowest === null) {
                rowPriceElement.textContent = "Sold out";
                return;
            }

            let label = `From ${price(lowest)}`;
            const optionInput = item.querySelector(".productV11ProductsItemOptionsItemInput:checked");
            if (optionInput) {
                // a bigger set is the same variant over again, so its price multiplies with it
                const variant = getOptionVariant(item.dataset.productId, optionInput, color);
                label = price(variant.price * getOptionQuantity(optionInput));
            }

            rowPriceElement.textContent = label;
        });
    }

    // the matched note only stands while an upsell still sits on the size it was matched to
    function renderMatchNotes() {
        const size = getMatchSize();

        section.querySelectorAll(".productV11ProductsItem.upsell").forEach(item => {
            const noteElement = item.querySelector(".productV11ProductsItemOptionsUpsellMatch");
            if (!noteElement) {
                return;
            }

            const input = item.querySelector(".productV11ProductsItemOptionsItemInput:checked");
            const matched = item.dataset.match === "auto" && !!input && input.dataset.size === size;
            noteElement.classList.toggle("active", matched);
            if (matched) {
                noteElement.textContent = noteElement.dataset.text.replace("[size]", input.value);
            }
        });
    }

    // with nothing ticked the header shows what the complete set costs, otherwise the running total
    function renderTotal(selections) {
        const color = getSelectedColor();
        let total = 0;

        if (selections.length > 0) {
            total = getSelectionsTotal(selections);
            priceElement.textContent = price(total);
        } else {
            products.forEach(product => {
                total += getLowestPrice(product.id, color) ?? 0;
            });
            priceElement.textContent = `From ${price(total)}`;
        }

        // check for global sale
        if (document.querySelector('.productGlobalSales')) {
            const salePrice = price(total - ((Number(document.querySelector('.productGlobalSales').dataset.globalSalePercentage) * total) / 100));
            document.querySelectorAll('.productGlobalSales').forEach(el => el.textContent = salePrice);
        }
        section.querySelector(".productV11ATCActivePrice").textContent = price(total, true);

        return total;
    }

    // the summary bar reads the bundle back as a sentence, and only opens once there is something in it
    function renderSummary(selections) {
        if (selections.length === 0) {
            summaryElement.classList.remove("active");
            return;
        }

        summaryElement.classList.add("active");

        // rows are listed in the order they appear above, so the sentence follows the same order
        const items = selections.map(selection => selection.summary);
        const last = items.pop();
        const list = items.length > 0 ? `${items.join(", ")} and ${last}` : last;

        summaryTextElement.textContent = `You are adding ${list} to your cart`;
    }

    // update the ui
    function render() {
        renderAvailability();
        matchUpsells();
        const selections = getSelections();
        renderRows();
        renderRowPrices();
        renderMatchNotes();
        renderSummary(selections);
        renderTotal(selections);

        // nothing selected means nothing to add
        atcElement.disabled = selections.length === 0;
    }

    /**************
     Events
     ***************/

    // handles all actions from adding/removing product variants
    section.addEventListener("click", function (e) {

        // color updates, change gallery and update availability
        if (e.target.closest(".productV11ColorsItemInput")) {
            // update the active color title
            colorTitleElement.textContent = section.querySelector(".productV11ColorsItemInput:checked")?.value ?? "";
            renderGallery();
            render();
            return;
        }

        // each row represents the options for the product
        const rowButton = e.target.closest(".productV11ProductsItemRow");
        if (rowButton) {
            const item = rowButton.closest(".productV11ProductsItem");

            // with nothing to pick the row is the only control, so it adds and removes the product itself
            if (!item.querySelector(".productV11ProductsItemOptionsItemInput")) {
                item.classList.toggle("added");
                render();
                return;
            }

            // when an item is closed, remove item from the added group. only on desktop, always showing on mobile
            if (isAdded(item)) {
                item.querySelectorAll(".productV11ProductsItemOptionsItemInput").forEach(input => input.checked = false);
                item.classList.remove("open");
            } else {
                item.classList.toggle("open");
            }

            // an upsell that just opened matches the bundle's size until the shopper picks one of their own
            if (item.classList.contains("upsell")) {
                item.dataset.match = "";
                if (item.classList.contains("open")) {
                    item.dataset.match = "auto";
                }
            }

            render();
            return;
        }


        const optionInput = e.target.closest(".productV11ProductsItemOptionsItemInput");
        if (optionInput) {
            const item = optionInput.closest(".productV11ProductsItem");
            // clicking a checked button will remove it from the added items
            if (item.dataset.selectedOption === optionInput.value) {
                optionInput.checked = false;
            }

            // a size picked or cleared by hand stops the upsell following the bundle
            if (item.classList.contains("upsell")) {
                item.dataset.match = "manual";
            }

            render();
        }
    });

    /**************
     Product ATC
     ***************/

    // a threshold counts everything being added, a whole set only counts the bundle products
    function countsTowardGift(selection) {
        return giftThreshold !== null || !selection.upsell;
    }

    // check for this order exceeding the threshold, or completing the set when there is no threshold
    function earnsGift(selections) {
        const giftSelections = selections.filter(countsTowardGift);

        // if no threshold, use product count
        if (giftThreshold === null) {
            return giftSelections.length === products.length;
        }
        return getSelectionsTotal(giftSelections) > giftThreshold;
    }

    atcElement.addEventListener("click", async function () {

        const selections = getSelections();
        if (selections.length === 0) {
            return;
        }

        // add loading animation
        this.classList.add("loading");

        // the gift only rides along on an order that earns it, and only when the cart hasn't got one
        let addGift = false;
        if (giftVariantID && earnsGift(selections)) {
            const cart = await getCart();
            addGift = !cart.items.some(item => item.id === giftVariantID);
        }

        const formData = {items: []};
        // tag items to group
        const groupID = Date.now();
        selections.forEach(selection => {
            // only lines that earned the gift pair with it, so on a whole set removing an upsell leaves the gift alone
            const properties = {
                _group_id: groupID,
            };
            if (addGift && countsTowardGift(selection)) {
                properties._gift_group = "purchase";
            }

            formData.items.push({
                quantity: selection.quantity,
                id: selection.variant.id,
                properties: properties,
            });
        });

        // the gift shares the group id so the cart can remove it with its bundle
        if (addGift) {
            formData.items.push({
                quantity: 1,
                id: giftVariantID,
                properties: {
                    _gift_group: "gift",
                    _group_id: groupID,
                },
            });
        }

        // add products - no form so use route
        await fetch(`${Shopify.routes.root}cart/add.js`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(formData)
        }).catch(err => console.log(err));

        // klaviyo tracking
        try {
            _learnq.push(["track", "Added to Cart", formData.items]);
        } catch (error) {
            console.error("Klaviyo tracking not found");
        }

        // remove loading animation
        this.classList.remove("loading");

        // open cart
        await openCart(null, groupID);

    });

    /**************
     Dropdowns
     ***************/

    const buttons = section.querySelectorAll('.productV11DropsItemButton');
    buttons?.forEach(element => {
        element.addEventListener("click", function () {
            this.classList.toggle("active");
            this.setAttribute("aria-expanded", String(this.classList.contains("active")));
        });
    });

    /**************
     Init
     ***************/
    render();
}());